import { ReactNode, useState } from "react";
import { Collapse } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const inputAlternatives: string[][] = [
  ["lossofhearingaccident", "lossofhearing"],
  ["lossofsightaccident", "lossofsight"],
  ["lossofspeechaccident", "lossofspeech"],
  ["paralysis", "paralysisaccident"],
  ["severburns", "severeburnsaccident"],
  ["coma", "comaaccident"],
  ["preexistexclutns", "preexistexclutnsprgnonrlt"],
];

const InputAlternativesHelperText = ({ matchedGroups }: { matchedGroups: string[][] }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <span>
      <span
        onClick={() => setExpanded(prev => !prev)}
        style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", fontWeight: 600 }}
      >
        {matchedGroups.length} input alternative{matchedGroups.length > 1 ? "s" : ""} detected
        {expanded ? <ExpandLessIcon fontSize="inherit" /> : <ExpandMoreIcon fontSize="inherit" />}
      </span>
      <Collapse in={expanded}>
        <span
          style={{
            display: "block",
            marginTop: 4,
            paddingLeft: 8,
            borderLeft: "2px solid rgba(0, 0, 0, 0.2)",
            fontWeight: 400,
          }}
        >
          {matchedGroups.map((group, gi) => (
            <span key={gi} style={{ display: "block" }}>
              {group.map((word, wi) => (
                <span key={word}>
                  <b>{word}</b>
                  {wi < group.length - 2 && ", "}
                  {wi === group.length - 2 && " and "}
                </span>
              ))}{" "}
              are treated as the same key
            </span>
          ))}
        </span>
      </Collapse>
    </span>
  );
};

export const getInputAlternativesHelperText = (value: string): ReactNode | undefined => {
  if (!value) return undefined;
  const lower = value.toLowerCase();
  const matchedGroups = inputAlternatives.filter(group =>
    group.some(word => lower.includes(word.toLowerCase())),
  );
  if (matchedGroups.length === 0) return undefined;
  return <InputAlternativesHelperText matchedGroups={matchedGroups} />;
};

export const camelCaseToWords = (s: string) => {
  const result = s.replace(/([A-Z])/g, " $1");
  return result.charAt(0).toUpperCase() + result.slice(1);
};

export const wordsToSnakeCase = (input: string) => {
  return input.replace(/,+/g, "").replace(/\s+/g, "_").toLowerCase();
};

export const inputDirectiveTooltip = (
  <div style={{ fontSize: 12, lineHeight: 1.6 }}>
    <div style={{ marginBottom: 8 }}>
      <b>Supported input directives:</b>
    </div>
    <div style={{ marginBottom: 6 }}>
      <code>[input_key:expected]</code>
      <br />
      Matches when payload[<code>key</code>] equals <code>expected</code>.
    </div>
    <div style={{ marginBottom: 6 }}>
      <code>[input_key:notexist]</code>
      <br />
      Matches when <code>key</code> does not exist in the payload.
    </div>
    <div style={{ marginBottom: 6 }}>
      <code>[input_key:not:expected]</code>
      <br />
      Matches when payload[<code>key</code>] does <b>not</b> equal <code>expected</code>.
    </div>
    <div>
      <code>[input_key:alt:expected]</code>
      <br />
      Matches when alt_payload[<code>key</code>] equals <code>expected</code>.
    </div>
  </div>
);

const validateDirective = (directive: string): boolean | string => {
  const colonCount = (directive.match(/:/g) ?? []).length;
  if (colonCount === 0) return true;

  if (colonCount === 1) {
    const [, match] = directive.split(":");
    const matcherValue = match.trim().replace(/^["'[\]]|["'[\]]$/g, "");
    return !matcherValue ? `Matcher value is required. Got "${matcherValue}"` : true;
  }

  // Expected format: [input_key:op:value]
  const [, modifier, modifierValue] = directive.match(/^\[.*?:([^:]*):([^\]]*)\]$/) ?? [];
  if (!modifier?.trim() || !modifierValue?.trim())
    return `Modifier is required. Got "${modifierValue}". Supported modifiers are "not" and "alt"`;

  return !["not", "alt"].includes(modifier.trim())
    ? `Unsupported modifier. Got "${modifier}". Supported modifiers are "not" and "alt"`
    : true;
};

export const inputValidator = (value: any): boolean | string => {
  if (typeof value !== "string") return false;

  const directives = value.match(/\[[^\]]*\]/g);
  if (!directives) return true;

  for (const directive of directives) {
    const result = validateDirective(directive);
    if (result !== true) return result;
  }
  return true;
};
